import React, { FunctionComponent } from 'react';

import clsx from 'clsx';

import { NutritionFacts, ProductDetails } from '@domains/product';

import { isValueSet } from '@utils/guards';
import { KopecksIso } from '@utils/isomorph';
import { getAvailabilityUrl } from '@utils/product/attributes';
import { applyTypograf } from '@utils/typograf';

import { useProduct } from '@hooks/use-product';
import { useProductAvailability } from '@hooks/use-product-availability';

import { Button } from '@components/common/Button/Button';
import { FavoriteButton } from '@components/common/FavoriteButton/FavoriteButton';
import { QuantityPicker } from '@components/common/QuantityPicker/QuantityPicker';
import { ProductPricePerUnit } from '@components/product/ProductPricePerUnit/ProductPricePerUnit';
import { ProductPricePresenter } from '@components/product/ProductPricePresenter/ProductPricePresenter';

import styles from './ProductInfoBlock.module.scss';

const hasSomeNutritionFacts = (nutritionFacts: NutritionFacts) =>
    Object.values(nutritionFacts).some((value) => value > 0);

type ProductInfoBlockProps = ProductDetails & {
    className?: string;
};

export const ProductInfoBlock: FunctionComponent<ProductInfoBlockProps> = (props) => {
    const {
        id,
        name,
        vendor_code,
        className,
        measure_unit,
        purchase_unit,
        is_novelty,
        is_promo,
        is_favorite,
        is_available,
        availability,
        nutrition_facts_per_100g,
    } = props;

    const { protein, fat, carbohydrate, calories } = nutrition_facts_per_100g;
    const hasNutritionFacts = hasSomeNutritionFacts(nutrition_facts_per_100g);

    const mappedAvailability = useProductAvailability(availability);

    const {
        cartItem,
        isDisabledCartInterface,
        isAddToCartPending,
        handleAddProductToCart,
        handleDebouncedQuantityChange,
        handleRemoveFromCart,
    } = useProduct(id);

    return (
        <div className={clsx(styles.root, className)}>
            <div className={styles.vendorCodeRow}>
                {isValueSet(vendor_code) && <span>Артикул: {vendor_code}</span>}
                {is_novelty && <div className={clsx(styles.badge, styles.isNew)}>Новинка</div>}
                {is_promo && <div className={clsx(styles.badge, styles.isPromo)}>Акция</div>}
                <meta itemProp="sku" content={vendor_code ?? ''} />
            </div>
            <h1 className={styles.title} itemProp="name">
                {applyTypograf(name)}
            </h1>
            <div className={styles.priceRow}>
                <div className={styles.prices}>
                    <ProductPricePerUnit
                        measureUnit={measure_unit}
                        className={styles.pricePerUnit}
                    />
                    <div itemProp="offers" itemScope itemType="https://schema.org/Offer">
                        <meta
                            itemProp="price"
                            content={String(KopecksIso.to(measure_unit.price_in_kopecks.new))}
                        />
                        <meta itemProp="priceCurrency" content="RUB" />
                        <link itemProp="availability" href={getAvailabilityUrl(is_available)} />
                        <ProductPricePresenter
                            purchaseUnit={purchase_unit}
                            measureUnitName={measure_unit.type.name}
                            classes={{
                                root: styles.priceBlock,
                                promotionPrice: styles.promotionPrice,
                                measureUnits: styles.measureUnits,
                            }}
                        />
                    </div>
                </div>
                <div className={styles.actions}>
                    {cartItem === null || mappedAvailability.isDisabledButton ? (
                        <Button
                            isFullWith
                            variant="green"
                            className={styles.cartButton}
                            onClick={() => handleAddProductToCart()}
                            disabled={
                                isDisabledCartInterface || mappedAvailability.isDisabledButton
                            }
                            isLoading={isAddToCartPending}
                        >
                            {mappedAvailability.buttonText}
                        </Button>
                    ) : (
                        <QuantityPicker
                            initialQuantity={cartItem.quantity}
                            step={purchase_unit.measure_units}
                            price={purchase_unit.price_in_kopecks.new}
                            unit={measure_unit.type.name}
                            className={styles.quantityPicker}
                            isDisabledInterface={isDisabledCartInterface}
                            onDebounceQuantityChange={handleDebouncedQuantityChange}
                            onRemoveAtZeroQuantity={handleRemoveFromCart}
                        />
                    )}

                    <FavoriteButton
                        type="product"
                        id={id}
                        initialIsFavorite={is_favorite}
                        className={styles.favoriteButton}
                        size={62}
                        isDisabled={mappedAvailability.isDisabledFavorite}
                    />
                </div>
            </div>
            <article
                className={clsx(styles.characteristicsBlock, !hasNutritionFacts && styles.isEmpty)}
            >
                <span className={styles.characteristicsTitle}>
                    {hasNutritionFacts
                        ? `Пищевая и\u00a0энергетическая ценность в\u00a0100\u00a0г.`
                        : 'Не\u00a0имеет пищевой энергетической ценности '}
                </span>

                {hasNutritionFacts && (
                    <div className={styles.characteristics}>
                        <div className={styles.characteristic}>
                            <span className={styles.characteristicLabel}>белки</span>
                            <span className={styles.characteristicValue}>{protein}</span>
                        </div>
                        <div className={styles.characteristic}>
                            <span className={styles.characteristicLabel}>жиры</span>
                            <span className={styles.characteristicValue}>{fat}</span>
                        </div>
                        <div className={styles.characteristic}>
                            <span className={styles.characteristicLabel}>углеводы</span>
                            <span className={styles.characteristicValue}>{carbohydrate}</span>
                        </div>
                        <div className={styles.characteristic}>
                            <span className={styles.characteristicLabel}>ккал</span>
                            <span className={styles.characteristicValue}>{calories}</span>
                        </div>
                    </div>
                )}
            </article>
        </div>
    );
};
